/**
 * Level 3, Lessons 4–6 skills: "same numbers trade freely" (the distributive property), adding 4 on the mini Map,
 * multiplying by tens ("find the small fact, then say it in tens"), and reading a clock to the minute.
 */
import { int, pick } from "../core";
import type { Item, Skill } from "../types";
import { clockFace, mathMap } from "../visuals";

const hit = (raw: string, n: number) => raw.trim().replace(/,/g, "") === String(n);

/* ---------- L3.4 Same numbers trade freely ---------- */
/** The book's splits break the SECOND factor into an anchor or a known fact plus the rest: ×5 + ×1, ×10 + ×2, ×5 + ×2, ×3 + ×4. */
export function distItem(a: number, b: number, first: number): Item {
  const rest = b - first, ans = a * b;
  return {
    id: `distributive-split:${a}x${b}=${first}+${rest}`,
    skill: "distributive-split",
    prompt: `Split it into two facts you know, then add: &nbsp; ${a} × ${b} = (${a} × ${first}) + (${a} × __) = __`,
    speak: `${a} times ${b} equals ${a} times ${first}, plus ${a} times blank. Then the answer.`,
    input: { kind: "fields", fields: [{ label: `(${a} × ${first}) + (${a} ×`, suffix: ")", width: 2 }, { label: "=", width: 3 }] },
    answer: `${rest}|${ans}`,
    diagnose: (raw) => {
      const [r, p] = raw.split("|").map((x) => Number(x));
      if (r === rest && p === a * first + rest) return { code: "dist-added-not-multiplied", hint: `The second part is ${a} × ${rest}, not just ${rest}. Two facts, then add them.` };
      if (r === rest && p !== ans) return { code: "dist-sum-slip", hint: `The split is right. Now ${a} × ${first} = ${a * first} and ${a} × ${rest} = ${a * rest}. Add them.` };
      if (r !== undefined && r + first !== b) return { code: "dist-split-wrong", hint: `The two parts have to make ${b}: ${first} and how many more?` };
      return null;
    },
    hints: [`${first} and how many more make ${b}?`, `${b} = ${first} + ${rest}. So it is ${a} × ${first} plus ${a} × ${rest}.`, `${a} × ${first} = ${a * first}, ${a} × ${rest} = ${a * rest}, and ${a * first} + ${a * rest} = ${ans}.`],
  };
}
export const distributive: Skill = {
  id: "distributive-split",
  name: "Same numbers trade freely",
  gen: (rng) => {
    const a = pick(rng, [3, 4, 6, 7, 8, 9]);
    const b = pick(rng, [6, 7, 8, 9, 11, 12]);
    const first = b >= 11 ? 10 : b === 7 && rng() < 0.4 ? 3 : 5;
    return distItem(a, b, first);
  },
};

/* ---------- L3.4 Adding 4: two jumps forward, same color ---------- */
export function add4Item(n: number): Item {
  const ans = n + 4;
  return {
    id: `add-4-mini-map:${n}+4`,
    skill: "add-4-mini-map",
    prompt: `${n} + 4 =`,
    visual: mathMap({ rows: 2, mark: [n] }),
    input: { kind: "number" },
    answer: String(ans),
    diagnose: (raw) => {
      if (hit(raw, n + 2)) return { code: "add4-one-jump", hint: "That's one jump. Adding 4 is two jumps, same color." };
      if (hit(raw, n + 3) || hit(raw, n + 5)) return { code: "add4-color", hint: `Land on the same color as ${n} each jump: skip the other color.` };
      return null;
    },
    hints: [`${n} is in a ${n % 2 ? "white" : "blue"} column. Jump forward to the next ${n % 2 ? "white" : "blue"} square.`, `Now jump again — same color. Two jumps.`, `${n} → ${n + 2} → ${ans}: two jumps forward, same color.`],
  };
}
export const add4: Skill = { id: "add-4-mini-map", name: "Add 4: two jumps, same color", gen: (rng) => add4Item(int(rng, 3, 9)) };

/* ---------- L3.5 Multiplying by tens ---------- */
export function timesTensItem(a: number, t: number): Item {
  const small = a * t, ans = small * 10;
  return {
    id: `times-tens:${a}x${t}0`,
    skill: "times-tens",
    prompt: `${a} × ${t}0 =`,
    input: { kind: "number" },
    answer: String(ans),
    diagnose: (raw) => {
      if (hit(raw, small)) return { code: "tens-small-fact-only", hint: `That's ${a} × ${t}. Now say it in tens: ${small} tens.` };
      if (hit(raw, small * 100)) return { code: "tens-two-zeros", hint: `Only one of the numbers has a zero: ${small} tens.` };
      return null;
    },
    hints: [`Find the small fact: ${a} × ${t}.`, `${a} × ${t} = ${small}. Now say it in tens.`, `${a} × ${t} = ${small}, so ${a} × ${t}0 is ${small} tens: ${ans}.`],
  };
}
export const timesTens: Skill = {
  id: "times-tens",
  name: "Multiply by tens",
  gen: (rng) => timesTensItem(pick(rng, [3, 4, 5, 6, 7, 8, 9]), int(rng, 2, 9)),
};

/* ---------- L3.6 Telling time to the minute ---------- */
export function clockItem(h: number, m: number): Item {
  const mm = String(m).padStart(2, "0");
  const fives = Math.floor(m / 5), extra = m % 5;
  return {
    id: `clock-read-minute:${h}:${mm}`,
    skill: "clock-read-minute",
    prompt: "What time is it?",
    visual: clockFace(h, m),
    input: { kind: "fields", fields: [{ width: 2 }, { label: ":", width: 2 }] },
    answer: `${h}|${mm}`,
    alsoAccept: [`${h}|${m}`],
    diagnose: (raw) => {
      const [H, M] = raw.split("|").map(Number);
      if (H === h + 1 && M === m) return { code: "clock-hour-next", hint: "The short hand hasn't reached the next number yet. Use the number it has passed." };
      if (H === h && M === fives * 5) return { code: "clock-no-ticks", hint: `Count by fives to the number, then count the extra ticks by ones.` };
      if (H === h && M === fives) return { code: "clock-number-not-minutes", hint: `The long hand is past the ${fives || 12}. Each number is worth five minutes: count by fives.` };
      return null;
    },
    hints: ["The short hand tells the hour. Which number has it passed?", `The long hand: count by fives to the ${fives || 12}${extra ? `, then the ${extra} extra tick${extra > 1 ? "s" : ""} by ones` : ""}.`, `Short hand past the ${h}; long hand ${fives * 5} + ${extra} = ${m} minutes: ${h}:${mm}.`],
  };
}
export const clockMinute: Skill = {
  id: "clock-read-minute",
  name: "Tell time to the minute",
  gen: (rng) => {
    let m = int(rng, 1, 58);
    while (m % 5 === 0) m = int(rng, 1, 58);
    return clockItem(int(rng, 1, 12), m);
  },
};

export const LEVEL3_L4TO6_SKILLS: Skill[] = [distributive, add4, timesTens, clockMinute];
